import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import config, { SUPPORT_EMAIL } from '../config';
import { useAuthContext } from '../contexts/AuthContext';
import { trackEvent } from '../lib/analytics';

/**
 * Contact support (task 607).
 *
 * A short authenticated form that POSTs straight to the intake Lambda (task
 * 606) with the caller's Keycloak access token — there is no innovate-service
 * endpoint in the path, because a support ticket is not account data and the
 * service should not become a mail relay.
 *
 * Identity is never typed. The email the reply goes to is read from the
 * Keycloak context and shown as read-only context, so a User cannot misaddress
 * their own ticket and support always has a verified address to answer.
 *
 * When `VITE_SUPPORT_LAMBDA_URL` is empty — the local-development posture,
 * since the Lambda exists only in preprod and prd — the form is replaced by a
 * mailto link rather than rendering a form whose submit could never land.
 *
 * Analytics: `support_ticket_submitted` carries the category and nothing else.
 * Subject, description, and identity never reach the analytics stream.
 */

interface SupportCategory {
  value: string;
  label: string;
}

const SUPPORT_CATEGORIES: SupportCategory[] = [
  { value: 'question', label: 'Question' },
  { value: 'problem', label: 'Problem' },
  { value: 'billing', label: 'Billing' },
  { value: 'feedback', label: 'Feedback' },
];

const SUBJECT_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 4000;

const INPUT_CLASSES =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white';

const LABEL_CLASSES = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

/** Response envelope the intake Lambda answers a successful POST with. */
interface SupportTicketResponse {
  success?: boolean;
  reference?: string;
}

export default function SupportPage() {
  const location = useLocation();
  const { user, token } = useAuthContext();
  const email = user?.email ?? '';

  /**
   * The page the User came from, handed over as router state by the Help menu
   * and the help assistant's unanswered-question affordance. It is shown as a
   * dismissible chip rather than sent silently: it is context about the User's
   * session, so they get to decide whether support sees it.
   */
  const fromState = (location.state as { from?: string } | null)?.from;
  const [fromDismissed, setFromDismissed] = useState(false);
  const fromPath = fromState && !fromDismissed ? fromState : undefined;

  const [category, setCategory] = useState(SUPPORT_CATEGORIES[0].value);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lambdaUrl = config.SUPPORT_LAMBDA_URL;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // The Keycloak access token the app already holds. The Lambda
          // validates it against the realm — no separate credential.
          Authorization: `Bearer ${token ?? ''}`,
        },
        body: JSON.stringify({
          category,
          subject,
          description,
          ...(fromPath ? { fromPath } : {}),
        }),
      });

      if (!response.ok) throw new Error(`Support intake responded ${response.status}`);

      const body = (await response.json()) as SupportTicketResponse;
      // Category only — never the subject, the description, or the identity.
      trackEvent('support_ticket_submitted', { category });
      setReference(body.reference ?? '');
    } catch {
      // Form values are deliberately left in state so a retry costs nothing.
      setError(
        'Your ticket could not be submitted. Please try again, or email us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Local development: no Lambda, so offer the inbox instead -------------
  if (!lambdaUrl) {
    return (
      <div>
        <PageHeader
          title="Contact support"
          description="Tell us what you need and we'll get back to you."
        />
        <div className="mt-6 max-w-2xl" data-testid="support-mailto-fallback">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Support form not available in local dev.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="support-mailto-link"
            className="mt-3 inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <EnvelopeIcon className="h-4 w-4 text-gray-400" />
            Open email client
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Contact support"
        description="Tell us what you need and we'll get back to you."
      />

      <div className="mt-6 max-w-2xl">
        {email && (
          <p
            className="text-sm text-gray-500 dark:text-gray-400"
            data-testid="support-identity"
          >
            We&apos;ll reply to you at {email}
          </p>
        )}

        {reference !== null ? (
          <div
            className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
            data-testid="support-success"
          >
            <p className="text-sm text-gray-900 dark:text-white">
              Ticket <strong data-testid="support-reference">{reference}</strong>{' '}
              submitted — we&apos;ll reply to you at {email}.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4" data-testid="support-form">
            {fromState && !fromDismissed && (
              <div
                className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                data-testid="support-from-chip"
              >
                Coming from: {fromState}
                <button
                  type="button"
                  aria-label="Remove page context"
                  data-testid="support-from-chip-dismiss"
                  onClick={() => setFromDismissed(true)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div>
              <label htmlFor="support-category" className={LABEL_CLASSES}>
                Category
              </label>
              <select
                id="support-category"
                data-testid="support-category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className={INPUT_CLASSES}
              >
                {SUPPORT_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="support-subject" className={LABEL_CLASSES}>
                Subject
              </label>
              <input
                id="support-subject"
                data-testid="support-subject"
                type="text"
                required
                maxLength={SUBJECT_MAX_LENGTH}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                className={INPUT_CLASSES}
              />
            </div>

            <div>
              <label htmlFor="support-description" className={LABEL_CLASSES}>
                Description
              </label>
              <textarea
                id="support-description"
                data-testid="support-description"
                required
                rows={8}
                maxLength={DESCRIPTION_MAX_LENGTH}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className={INPUT_CLASSES}
              />
            </div>

            {error && (
              <p
                className="text-sm text-red-600 dark:text-red-400"
                data-testid="support-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="support-submit"
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting…' : 'Submit ticket'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
